import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, MessagesSquare } from 'lucide-react';
import { DetailSection, DetailRow, LinkChip, ChipGrid, StatusBadge } from './detail-primitives';

const ClassroomDetail = ({ data, onOpen, onNavigate }) => {
  const navigate = useNavigate();

  const go = (path) => {
    // Close the modal stack + search palette first so the user lands
    // on the destination route with no overlays still visible, then
    // navigate via React Router (same routes the rest of the app uses).
    if (onNavigate) onNavigate();
    navigate(path);
  };

  const cid = data.id;
  const canEnter = !!cid;

  return (
    <div className="space-y-3" data-testid="classroom-detail-body">
      <DetailSection title="بطاقة القاعة">
        <DetailRow label="اسم القاعة" value={data.classroom_name} testId="classroom-name" />
        <DetailRow label="كود القاعة" value={data.classroom_code} testId="classroom-code" />
        <DetailRow label="الحالة" value={<StatusBadge status={data.status} />} />
        <DetailRow label="المجموعة" value={data.group_name} />
        <DetailRow label="الجدول" value={data.schedule} testId="classroom-schedule" />
        <DetailRow label="بداية الجلسة" value={data.session_start} />
        <DetailRow label="نهاية الجلسة" value={data.session_end} />
      </DetailSection>

      <DetailSection title="المنهج">
        <DetailRow label="البرنامج" value={data.program_name} />
        <DetailRow label="المستوى" value={data.level_name} />
        <DetailRow label="الكتاب" value={data.book_name} />
        <DetailRow label="الوحدة الحالية" value={data.current_unit_name} />
        <DetailRow label="الدرس الحالي" value={data.current_lesson_name} />
      </DetailSection>

      <DetailSection title="الفريق">
        {data.teacher_id ? (
          <div className="flex items-start gap-3 py-1">
            <span className="text-[11px] text-[#5C6B8C] min-w-[110px] shrink-0 pt-0.5">المعلم</span>
            <LinkChip
              kind="teacher"
              id={data.teacher_id}
              onOpen={onOpen}
              label={data.teacher_name || 'فتح بطاقة المعلم'}
              sub={data.teacher_phone || undefined}
              testId={`classroom-teacher-link-${data.teacher_id}`}
            />
          </div>
        ) : (
          <DetailRow label="المعلم" value={data.teacher_name} />
        )}
        <DetailRow label="المشرف الأكاديمي" value={data.supervisor_name} />
      </DetailSection>

      <DetailSection title={`الطلاب (${data.current_students ?? (data.students || []).length} / ${data.max_students ?? '∞'})`}>
        <ChipGrid empty="لا يوجد طلاب في هذه القاعة بعد.">
          {(data.students || []).map((s) => (
            <LinkChip
              key={s.id}
              kind="student"
              id={s.id}
              onOpen={onOpen}
              label={s.full_name || 'طالب'}
              sub={[s.phone_number, s.status].filter(Boolean).join(' • ')}
              testId={`classroom-student-link-${s.id}`}
            />
          ))}
        </ChipGrid>
      </DetailSection>

      {/* Sticky quick-action footer — June 2026
          Two actions reuse the canonical routes already wired into the
          rest of the app, so admins can jump from the search palette
          straight into a session or its chat group. */}
      <div
        className="sticky bottom-0 -mx-4 -mb-4 mt-1 px-4 py-3 bg-white border-t border-[#E2E4E0] flex flex-col sm:flex-row gap-2"
        data-testid="classroom-quick-actions"
      >
        <button
          type="button"
          disabled={!canEnter}
          onClick={() => go(`/voice-room/${cid}`)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-[#0A1128] text-[#C9A24E] hover:bg-[#1a2342] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="classroom-enter-session-btn"
        >
          <Radio size={15} />
          دخول إلى الحصة
        </button>
        <button
          type="button"
          disabled={!canEnter}
          onClick={() => go(`/classroom-groups?room=${cid}`)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-white text-[#0A1128] border border-[#0A1128] hover:bg-[#0A1128] hover:text-[#C9A24E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="classroom-enter-group-btn"
        >
          <MessagesSquare size={15} />
          دخول إلى مجموعة القاعة
        </button>
      </div>
    </div>
  );
};

export default ClassroomDetail;
